import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './redux/store';
import { setUser } from './redux/slices/userSlice';
import { getCurrentUser } from './services/auth-service';
import { setNavigationHandler } from './services/api-client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { CartProvider } from './contexts/CartContext';
import { SnackbarProvider } from 'notistack';

// Layout components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AdminLayout from './components/AdminLayout/AdminLayout';

// Auth pages
import LoginPage from './pages/LoginPage/LoginPage';
import RegisterPage from './pages/RegisterPage/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage/ResetPasswordPage';
import FinishFormalizationPage from './pages/FinishFormalizationPage/FinishFormalizationPage';

// Dashboard pages
import DashboardPage from './pages/DashboardPage/DashboardPage';

// Customer pages
import CustomersPage from './pages/CustomersPage/CustomersPage';
import CustomerDetailsPage from './pages/CustomerDetailsPage/CustomerDetailsPage';
import CustomerFormPage from './pages/CustomerFormPage/CustomerFormPage';

// Opportunity pages
import OpportunitiesPage from './pages/OpportunitiesPage/OpportunitiesPage';
import OpportunityDetailsPage from './pages/OpportunityDetailsPage/OpportunityDetailsPage';
import OpportunityFormPage from './pages/OpportunityFormPage/OpportunityFormPage';

// Quote pages
import QuotesPage from './pages/QuotesPage/QuotesPage';
import QuoteDetailsPage from './pages/QuoteDetailsPage/QuoteDetailsPage';
import QuoteFormPage from './pages/QuoteFormPage/QuoteFormPage';

// Order pages
import OrdersPage from './pages/OrdersPage/OrdersPage';
import OrderDetailsPage from './pages/OrderDetailsPage/OrderDetailsPage';

// Product pages (admin only)
import ProductsPage from './pages/ProductsPage/ProductsPage';
import ProductDetailsPage from './pages/ProductDetailsPage/ProductDetailsPage';
import ProductFormPage from './pages/ProductFormPage/ProductFormPage';

// Store pages
import ResellerStorePage from './pages/ResellerStorePage/ResellerStorePage';
import CampaignsPage from './pages/CampaignsPage/CampaignsPage';
import CampaignDetailsPage from './pages/CampaignDetailsPage/CampaignDetailsPage';

// Admin pages
import AdminDashboardPage from './pages/Admin/AdminDashboardPage/AdminDashboardPage';
import AdminOpportunitiesPage from './pages/Admin/AdminOpportunitiesPage/AdminOpportunitiesPage';
import AdminQuotesPage from './pages/Admin/AdminQuotesPage/AdminQuotesPage';
import AdminQuoteDetailsPage from './pages/Admin/AdminQuoteDetailsPage';
import AdminOrdersPage from './pages/Admin/AdminOrdersPage/AdminOrdersPage';
import AdminProductsPage from './pages/Admin/AdminProductsPage/AdminProductsPage';
import AdminProductDetailsPage from './pages/Admin/AdminProductsPage/AdminProductDetailsPage';
import AdminUsersPage from './pages/Admin/AdminUsersPage/AdminUsersPage';
import AdminTemplatesPage from './pages/Admin/AdminTemplatesPage/AdminTemplatesPage';
import AdminTemplateNewPage from './pages/Admin/AdminTemplatesPage/AdminTemplateNewPage';
import AdminTemplateDetailsPage from './pages/Admin/AdminTemplatesPage/AdminTemplateDetailsPage';
import AdminOrderAcerStatusPage from './pages/Admin/AdminOrderAcerStatusPage';
import AdminCampaignsPage from './pages/Admin/AdminCampaignsPage/AdminCampaignsPage';
import AdminCampaignDetailsPage from './pages/Admin/AdminCampaignsPage/AdminCampaignDetailsPage';
import AdminCampaignResellersPage from './pages/Admin/AdminCampaignsPage/AdminCampaignResellersPage';
import LandingPageLogin from './pages/LandingPageLogin/LandingPageLogin';
import LandingPageCampaign from 'pages/LandingPageCampaign/LandingPageCampaign';

// Create a custom theme with Acer colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#83b81a', // Acer green
    },
    secondary: {
      main: '#333333', // Acer dark
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h5: {
      fontWeight: 500,
      color: '#2b3445',
      marginBottom: '0.5rem',
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        maxWidthLg: {
          maxWidth: '1360px',
          '@media (min-width:1200px)': {
            maxWidth: '1360px',
          },
        },
      },
    },
  },
});

const AppContent: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.user);

  // Set up navigation handler
  useEffect(() => {
    setNavigationHandler((path: string) => navigate(path));
  }, [navigate]);

  // Check if user is already logged in and redirect based on role
  useEffect(() => {
    const token = localStorage.getItem('token');
    const currentPath = window.location.pathname;

    // Skip token validation on auth-related paths
    const authPaths = [
      '/login',
      '/register',
      '/forgot-password',
      '/reset-password',
      '/finish-formalization',
    ];
    if (authPaths.some(path => currentPath.startsWith(path))) {
      console.log('On auth path, skipping token validation');
      return;
    }

    // If there's a token in localStorage, validate it with the backend
    if (token) {
      const validateToken = async () => {
        const userData = await getCurrentUser();
        if (userData) {
          console.log('Token validated, user data:', userData);
          dispatch(setUser(userData));
        } else {
          console.log('Token invalid or expired');
        }
      };

      validateToken();
    }
  }, [dispatch]);

  // Effect to redirect user based on role after authentication
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('App.tsx - Handling user redirection, user role:', user.role);
      const currentPath = window.location.pathname;

      // Don't redirect users on non-auth pages like password reset
      const nonAuthPaths = [
        '/forgot-password',
        '/reset-password',
        '/login',
        '/register',
        '/finish-formalization',
      ];
      if (nonAuthPaths.some(path => currentPath.startsWith(path))) {
        console.log('User on non-auth path, skipping role-based redirects');
        return;
      }

      // ADMIN USER ACCESS CONTROL
      if (user.role === 'admin') {
        // Only redirect admin if they're on the root path
        if (currentPath === '/') {
          console.log('ADMIN: Redirecting to admin dashboard from root');
          navigate('/admin');
          return;
        }

        // FIXED: Don't redirect admin on page reload if they're already on an admin page
        if (currentPath.startsWith('/admin')) {
          console.log('ADMIN: Already on admin page, not redirecting');
          return;
        }
      }

      // RESELLER USER ACCESS CONTROL
      if (user.role === 'reseller') {
        // Reseller should NEVER be on admin pages
        if (currentPath.startsWith('/admin')) {
          console.log('RESELLER: Blocked from admin area, redirecting to home');
          navigate('/');
          return;
        }

        // FIXED: Don't redirect reseller on page reload if they're already on a valid page
        // This prevents always going back to root on page reload
        if (currentPath !== '/') {
          console.log('RESELLER: Already on a valid page, not redirecting');
          return;
        }
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Determine the redirect path based on user role
  const getRedirectPath = () => {
    if (user?.role === 'admin') {
      return '/admin';
    }
    return '/';
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <BreadcrumbProvider>
          <CartProvider>
            <Routes>
              <Route path="/login" element={<LandingPageLogin />} />
              <Route path="/campaign" element={<LandingPageCampaign />} />
              <Route path="/campaign/:identifier" element={<LandingPageCampaign />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/:search_term/finish-formalization"
                element={<FinishFormalizationPage />}
              />
              {/* Main Routes (Reseller Routes) */}
              <Route
                path="/"
                element={
                  <ProtectedRoute requiredRole="reseller">
                    <Layout />
                  </ProtectedRoute>
                }
              >
                {/* Store as root page */}
                <Route index element={<ResellerStorePage />} />

                {/* Dashboard moved to /home */}
                <Route path="home" element={<DashboardPage />} />

                {/* Customers */}
                <Route path="customers" element={<CustomersPage />} />
                <Route path="customers/new" element={<CustomerFormPage />} />
                <Route path="customers/:id" element={<CustomerDetailsPage />} />
                <Route path="customers/:id/edit" element={<CustomerFormPage />} />

                {/* Opportunities */}
                <Route path="opportunities" element={<OpportunitiesPage />} />
                <Route path="opportunities/new" element={<OpportunityFormPage />} />
                <Route path="opportunities/:id" element={<OpportunityDetailsPage />} />
                <Route path="opportunities/:id/edit" element={<OpportunityFormPage />} />

                {/* Quotes */}
                <Route path="quotes" element={<QuotesPage />} />
                <Route path="quotes/new" element={<QuoteFormPage />} />
                <Route path="quotes/:id" element={<QuoteDetailsPage />} />
                <Route path="quotes/:id/edit" element={<QuoteFormPage />} />

                {/* Orders */}
                <Route path="orders" element={<OrdersPage />} />
                <Route path="orders/:id" element={<OrderDetailsPage />} />

                {/* Campaigns */}
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="campaigns/:campaign_id" element={<CampaignDetailsPage />} />
              </Route>

              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboardPage />} />
                <Route path="opportunities" element={<AdminOpportunitiesPage />} />
                <Route path="quotes" element={<AdminQuotesPage />} />
                <Route path="quotes/:id" element={<AdminQuoteDetailsPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="orders/:id/acer-status" element={<AdminOrderAcerStatusPage />} />
                <Route path="products" element={<AdminProductsPage />} />
                <Route path="templates" element={<AdminTemplatesPage />} />
                <Route path="campaigns" element={<AdminCampaignsPage />} />
                <Route
                  path="campaigns/:template_id/resellers"
                  element={<AdminCampaignResellersPage />}
                />
                <Route path="campaigns/:campaign_id" element={<AdminCampaignDetailsPage />} />
                <Route path="templates/new" element={<AdminTemplateNewPage />} />
                <Route path="templates/:id" element={<AdminTemplateDetailsPage />} />
                <Route path="users" element={<AdminUsersPage />} />

                {/* Admin product detail/creation routes */}
                <Route path="products/new" element={<ProductFormPage />} />
                <Route path="products/:id" element={<AdminProductDetailsPage />} />
                <Route path="products/:id/edit" element={<ProductFormPage />} />
              </Route>

              {/* Fallback route - redirect based on role */}
              <Route path="*" element={<Navigate to={getRedirectPath()} replace />} />
            </Routes>
          </CartProvider>
        </BreadcrumbProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
